import { useLiveQuery } from 'dexie-react-hooks';
import { getProposalBundle } from '@/db/proposalBundles';
import type { ProposalBundleRecord, ProposalDeltaOp } from '@/db/codexDB';
import type { ProposalPatchOp } from '@/lib/schemas/proposal.schema';
import { useConsoleStore } from '@/stores/consoleStore';
import { CardShell } from './CardShell';
import { CanonRefChips, SEVERITY_LABEL } from './cardParts';

function deltaLabel(d: ProposalDeltaOp): string {
  if (d.op === 'set_fact') return `+ 사실 추가 [${d.scope}] «${d.fact}»`;
  if (d.op === 'clear_fact') return `− 사실 빼기 [${d.scope}] «${d.fact}»`;
  if (d.op === 'rename_fact') return `± 사실 교체 [${d.scope}] «${d.from}» → «${d.to}»`;
  if (d.op === 'collapse_resource') return `⤓ 자원 붕괴 «${d.resource}» (${d.scope}부터)`;
  if (d.op === 'uncollapse_resource') return `⤒ 자원 붕괴 취소 «${d.resource}» (${d.scope})`;
  return `◌ 제도 상태 «${d.institution}» → ${d.status}`;
}

function patchLabel(p: ProposalPatchOp): string {
  if (p.kind === 'entity_field') return `✎ «${p.entity}».${p.field} ← ${String(p.value).slice(0, 40)}`;
  if (p.kind === 'entity_name') return `✎ «${p.entity}» 이름 → «${p.name}»`;
  if (p.kind === 'entity_summary') return `✎ «${p.entity}» 요약 ← ${p.summary.slice(0, 40)}`;
  if (p.kind === 'entity_tags') return `✎ «${p.entity}» 태그 [${p.tags.join(', ').slice(0, 40)}]`;
  if (p.kind === 'add_link') return `+ 관계 ${p.from} —${p.type}— ${p.to}`;
  if (p.kind === 'remove_link') return `− 관계 ${p.from} —${p.type}— ${p.to}`;
  return `✎ 프로젝트.${p.key} ← ${p.value.slice(0, 40)}`;
}

/** 콘솔 타임라인의 type='proposal' 이벤트 → 승인 큐 레코드를 라이브 조회해 렌더(결정 즉시 반영). */
export function ProposalEventCard({ proposalId }: { proposalId: string }) {
  const record = useLiveQuery(() => getProposalBundle(proposalId), [proposalId]);
  const decide = useConsoleStore((s) => s.decideProposal);
  if (!record) return null;
  return <ProposalBundleCard record={record} onDecide={(accepted) => void decide(record.id, accepted)} />;
}

/**
 * 커널 제안 묶음(K1) — Δ 1묶음 + 파급(새 충돌) + 지점별 제안이 한 카드로.
 * 수락만이 canon을 바꾼다(applyProposalDelta) — 적극적 에이전트, 확정은 작가(불변원칙 1).
 */
export function ProposalBundleCard({
  record,
  onDecide,
}: {
  record: ProposalBundleRecord;
  onDecide: (accepted: boolean) => void;
}) {
  const pending = record.status === 'proposed';
  const isScene = record.kind === 'scene' && record.scene;
  return (
    <CardShell
      title={`커널 제안 — ${record.title}`}
      at={record.createdAt}
      summary={
        <>
          {isScene ? (
            <>
              씬 → <span className="ref-chip">{record.scene!.beatId}</span> · {record.scene!.prose.length}자 · 봉인 게이트 통과
            </>
          ) : record.kind === 'patch' ? (
            <>구체 수정 {record.patches?.length ?? 0}건 — 엔티티·관계·메타</>
          ) : (
            <>
              변경 {record.delta.length} · 파급(새 충돌){' '}
              <b className={record.ripple.length ? 'text-amber-300' : 'text-green-300'}>{record.ripple.length}</b> · 지점 제안{' '}
              {record.fixes.length}
            </>
          )}
          {!pending && (
            <span className={`chip ml-2 ${record.status === 'accepted' ? 'green' : 'neutral'}`}>
              {record.status === 'accepted' ? '수락됨' : '뺌'}
            </span>
          )}
        </>
      }
    >
      {record.intent && <p className="text-[11px] leading-5 text-fainter">의도: {record.intent}</p>}

      {isScene && (
        <details open className="rounded border border-line bg-bg/60 p-2">
          <summary className="cursor-pointer font-mono text-[10px] text-fainter hover:text-dim">산문 {record.scene!.prose.length}자</summary>
          <p className="mt-1 max-h-72 overflow-y-auto whitespace-pre-wrap font-serif text-[13px] leading-7 text-ink">
            {record.scene!.prose}
          </p>
        </details>
      )}

      {record.delta.length > 0 && (
        <ul className="space-y-1">
          {record.delta.map((d, i) => (
            <li key={i} className="rounded border border-star/30 bg-star/5 px-2 py-1 text-[11px] leading-4 text-dim">
              {deltaLabel(d)}
            </li>
          ))}
        </ul>
      )}

      {(record.patches?.length ?? 0) > 0 && (
        <ul className="space-y-1">
          {record.patches!.map((pt, i) => (
            <li key={i} className="rounded border border-star/30 bg-star/5 px-2 py-1 text-[11px] leading-4 text-dim">
              {patchLabel(pt)}
            </li>
          ))}
        </ul>
      )}

      {record.ripple.length > 0 && (
        <div>
          <div className="field-key mb-1">파급 — 이 변경이 깨뜨리는 지점</div>
          <ul className="space-y-1.5">
            {record.ripple.map((r, i) => (
              <li key={i} className="rounded border border-line bg-bg/60 p-2">
                <div className="mb-0.5 flex items-center gap-1.5">
                  <span className={`mono text-[10px] ${r.severity === 'critical' ? 'text-red-300' : 'text-amber-300'}`}>
                    {SEVERITY_LABEL[r.severity] ?? r.severity}
                  </span>
                  <span className="mono text-[10px] text-fainter">{r.kind}</span>
                  <span className="ref-chip">{r.beat}</span>
                </div>
                <p className="text-[11px] leading-4 text-dim">{r.evidence}</p>
                <CanonRefChips refs={r.canon_refs} />
              </li>
            ))}
          </ul>
        </div>
      )}

      {record.fixes.length > 0 && (
        <div>
          <div className="field-key mb-1">지점별 수정 제안</div>
          <ul className="space-y-1.5">
            {record.fixes.map((f, i) => (
              <li key={i} className="rounded border border-line bg-bg/60 p-2">
                <span className="ref-chip">{f.beatId}</span>
                <p className="mt-1 text-[11px] leading-4 text-dim">{f.suggestion}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {pending ? (
        <div className="grid grid-cols-2 gap-1.5">
          <button
            type="button"
            onClick={() => onDecide(true)}
            className="rounded border border-line px-2 py-1.5 text-[11px] text-dim transition hover:border-green-400 hover:text-green-300"
          >
            {isScene ? '수락 — 비트 초안에 이어쓰기' : '수락 — canon 반영'}
          </button>
          <button
            type="button"
            onClick={() => onDecide(false)}
            className="rounded border border-line px-2 py-1.5 text-[11px] text-dim transition hover:border-red-400 hover:text-red-300"
          >
            빼기
          </button>
        </div>
      ) : (
        record.appliedNotes &&
        record.appliedNotes.length > 0 && (
          <details className="border-t border-line pt-1.5">
            <summary className="cursor-pointer text-[10px] text-fainter hover:text-dim">적용 기록 {record.appliedNotes.length}건</summary>
            <ul className="mt-1 space-y-0.5">
              {record.appliedNotes.map((n, i) => (
                <li key={i} className="font-mono text-[10px] leading-4 text-fainter">
                  {n}
                </li>
              ))}
            </ul>
          </details>
        )
      )}
    </CardShell>
  );
}
